/**
 * Theme color utilities for organization customization.
 * Used to derive visual-separation tokens without altering
 * administrator-selected --theme-primary / --theme-primary-text.
 */

/** Typical content/card surface behind primary UI (Skote cards are white). */
export const APP_SURFACE_BACKGROUND = "#ffffff";

/** Page body background from theme ($body-bg). */
export const APP_BODY_BACKGROUND = "#f1f5f7";

/** UI non-text contrast below this needs extra separation (WCAG UI ~3:1; we use a lower gate for "visible edge"). */
export const PRIMARY_SURFACE_CONTRAST_THRESHOLD = 1.45;

const DEFAULT_PRIMARY = "#5664d2";

const parseHexChannel = (hex, start, length) => {
    const raw = hex.slice(start, start + length);
    const value = length === 1 ? `${raw}${raw}` : raw;
    return parseInt(value, 16);
};

/**
 * Parse a CSS color string into { r, g, b } (0–255).
 * Supports #rgb, #rrggbb, rgb(), rgba(). Returns null if unparseable.
 */
export const parseCssColor = (color) => {
    if (!color || typeof color !== "string") {
        return null;
    }

    const value = color.trim().toLowerCase();

    if (value.startsWith("#")) {
        const hex = value.slice(1);
        if (/^[0-9a-f]{3}$/i.test(hex)) {
            return {
                r: parseHexChannel(hex, 0, 1),
                g: parseHexChannel(hex, 1, 1),
                b: parseHexChannel(hex, 2, 1),
            };
        }
        if (/^[0-9a-f]{6}$/i.test(hex)) {
            return {
                r: parseHexChannel(hex, 0, 2),
                g: parseHexChannel(hex, 2, 2),
                b: parseHexChannel(hex, 4, 2),
            };
        }
        return null;
    }

    const rgbMatch = value.match(
        /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/
    );
    if (rgbMatch) {
        return {
            r: Math.min(255, Math.max(0, Number(rgbMatch[1]))),
            g: Math.min(255, Math.max(0, Number(rgbMatch[2]))),
            b: Math.min(255, Math.max(0, Number(rgbMatch[3]))),
        };
    }

    return null;
};

const srgbChannelToLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG 2.1 relative luminance for an { r, g, b } color. */
export const getRelativeLuminance = (rgb) => {
    if (!rgb) {
        return 0;
    }

    const r = srgbChannelToLinear(rgb.r);
    const g = srgbChannelToLinear(rgb.g);
    const b = srgbChannelToLinear(rgb.b);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two CSS colors (or rgb objects). */
export const getContrastRatio = (colorA, colorB) => {
    const rgbA = typeof colorA === "string" ? parseCssColor(colorA) : colorA;
    const rgbB = typeof colorB === "string" ? parseCssColor(colorB) : colorB;

    if (!rgbA || !rgbB) {
        return 1;
    }

    const luminanceA = getRelativeLuminance(rgbA);
    const luminanceB = getRelativeLuminance(rgbB);
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);

    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Whether primary needs extra separation against app surfaces.
 * Checks both white content surfaces and the page body background.
 */
export const primaryNeedsSurfaceSeparation = (
    primaryColor,
    {
        surfaces = [APP_SURFACE_BACKGROUND, APP_BODY_BACKGROUND],
        threshold = PRIMARY_SURFACE_CONTRAST_THRESHOLD,
    } = {}
) => {
    const primary = parseCssColor(primaryColor) || parseCssColor(DEFAULT_PRIMARY);

    return surfaces.some((surface) => {
        const ratio = getContrastRatio(primary, surface);
        return ratio < threshold;
    });
};

/** Mix a color toward black by `amount` (0–1). Used to derive a border when needed. */
export const mixColorTowardBlack = (color, amount = 0.22) => {
    const rgb = parseCssColor(color) || parseCssColor(DEFAULT_PRIMARY);
    const mix = Math.min(1, Math.max(0, amount));
    const r = Math.round(rgb.r * (1 - mix));
    const g = Math.round(rgb.g * (1 - mix));
    const b = Math.round(rgb.b * (1 - mix));
    const toHex = (channel) => channel.toString(16).padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Derived theme tokens for primary/surface separation.
 * Does not alter the selected primary or primary-text colors.
 *
 * - borderColor: subtle edge on Primary-filled surfaces
 * - outlineColor: stronger foreground for outline buttons (transparent BG)
 * Both reuse the same contrast gate; only the mix amount differs.
 */
export const getPrimarySurfaceSeparationTokens = (primaryColor) => {
    const primary = primaryColor || DEFAULT_PRIMARY;
    const needsSeparation = primaryNeedsSurfaceSeparation(primary);

    if (!needsSeparation) {
        return {
            needsSeparation: false,
            borderColor: primary,
            outlineColor: primary,
            surfaceShadow: "none",
        };
    }

    return {
        needsSeparation: true,
        borderColor: mixColorTowardBlack(primary, 0.22),
        // Stronger mix so outline border/text stay readable on light pages
        outlineColor: mixColorTowardBlack(primary, 0.5),
        surfaceShadow: "0 1px 2px color-mix(in srgb, #000 10%, transparent)",
    };
};

/**
 * Apply derived primary separation CSS variables on :root.
 * Leaves --theme-primary / --theme-primary-text / --color-* untouched.
 */
export const applyPrimarySurfaceSeparationVars = (
    primaryColor,
    root = typeof document !== "undefined" ? document.documentElement : null
) => {
    if (!root) {
        return getPrimarySurfaceSeparationTokens(primaryColor);
    }

    const tokens = getPrimarySurfaceSeparationTokens(primaryColor);

    root.style.setProperty("--theme-primary-border", tokens.borderColor);
    root.style.setProperty("--theme-primary-outline", tokens.outlineColor);
    root.style.setProperty("--theme-primary-surface-shadow", tokens.surfaceShadow);
    root.setAttribute(
        "data-theme-primary-contrast",
        tokens.needsSeparation ? "low" : "ok"
    );

    return tokens;
};

/**
 * Convert a CSS color to a Bootstrap RGB channel list: "r, g, b".
 * Used for --bs-primary-rgb so .bg-primary / .text-primary stay theme-aware.
 */
export const cssColorToRgbChannels = (color) => {
    const rgb = parseCssColor(color) || parseCssColor(DEFAULT_PRIMARY);
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
};

/**
 * Sync Bootstrap Primary CSS variables with the org Primary Color.
 * Enables .bg-primary / .text-primary (and other --bs-primary-rgb consumers)
 * without changing semantic success/warning/danger/info tokens.
 * Does not alter --theme-primary / --theme-primary-text.
 */
export const applyBootstrapPrimaryUtilityVars = (
    primaryColor,
    root = typeof document !== "undefined" ? document.documentElement : null
) => {
    const primary = primaryColor || DEFAULT_PRIMARY;
    const channels = cssColorToRgbChannels(primary);

    if (!root) {
        return { primary, channels };
    }

    root.style.setProperty("--bs-primary", primary);
    root.style.setProperty("--bs-primary-rgb", channels);

    return { primary, channels };
};
